<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const winRatePercentage = computed(() => {
  return props.data?.win_rate || 0;
});

const winRateColor = computed(() => {
  const rate = winRatePercentage.value;
  if (rate >= 70) return 'text-n-teal-11 bg-n-teal-3 border-n-teal-6';
  if (rate >= 40) return 'text-n-amber-11 bg-n-amber-3 border-n-amber-6';
  return 'text-n-ruby-11 bg-n-ruby-3 border-n-ruby-6';
});
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
    <!-- Header -->
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t('PIPELINE.ANALYTICS.WIN_RATE.TITLE') }}
      </h3>
      <Icon icon="i-lucide-check-circle" class="size-5 text-n-slate-9" />
    </div>

    <!-- Main Metric -->
    <div class="mb-5">
      <div
        class="inline-flex items-baseline gap-2 rounded-lg border px-3 py-1.5"
        :class="[winRateColor]"
      >
        <span class="text-3xl font-semibold">
          {{ winRatePercentage.toFixed(1) }}%
        </span>
        <span class="text-xs font-medium">{{
          $t('PIPELINE.ANALYTICS.WIN_RATE.PERCENTAGE')
        }}</span>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-2 gap-3">
      <div class="rounded-lg bg-n-teal-3 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.WIN_RATE.TOTAL_WON') }}
        </div>
        <div class="text-xl font-semibold text-n-teal-11">
          {{ data?.total_won || 0 }}
        </div>
      </div>

      <div class="rounded-lg bg-n-ruby-3 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.WIN_RATE.TOTAL_LOST') }}
        </div>
        <div class="text-xl font-semibold text-n-ruby-11">
          {{ data?.total_lost || 0 }}
        </div>
      </div>

      <div class="rounded-lg bg-n-alpha-2 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.WIN_RATE.TOTAL_CLOSED') }}
        </div>
        <div class="text-xl font-semibold text-n-slate-12">
          {{ data?.total_closed || 0 }}
        </div>
      </div>

      <div class="rounded-lg bg-n-blue-3 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.WIN_RATE.TOTAL_OPEN') }}
        </div>
        <div class="text-xl font-semibold text-n-blue-11">
          {{ data?.total_open || 0 }}
        </div>
      </div>
    </div>

    <!-- By Pipeline (if available) -->
    <div
      v-if="data?.by_pipeline && data.by_pipeline.length > 0"
      class="mt-5 border-t border-n-weak pt-4"
    >
      <h4 class="mb-3 text-xs font-medium text-n-slate-11">
        {{ $t('PIPELINE.ANALYTICS.WIN_RATE.BY_PIPELINE') }}
      </h4>
      <div class="space-y-2">
        <div
          v-for="pipeline in data.by_pipeline"
          :key="pipeline.pipeline_id"
          class="flex items-center justify-between rounded-lg bg-n-alpha-2 px-3 py-2"
        >
          <span class="text-sm text-n-slate-11">{{
            pipeline.pipeline_name
          }}</span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ pipeline.win_rate.toFixed(1) }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
